import cx from 'clsx';
import * as Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import React from 'react';
import { useParams } from 'react-router-dom';

import SkeletonBlock from '@components/common/Skeleton/SkeletonBlock';
import Tabs from '@components/uikit/Tabs/Tabs';

import { getFailureCauseCategoriesStatusTabItems } from '@helpers/getFailureCauseCategoriesStatusTabItems';

import useTranslation from '@hooks/useTranslation';

import {
  selectFailureCauseCategoriesStatusFilter,
  useFailureCauseCategoriesActions,
} from '@pages/FailureCauseCategories/store/useFailureCauseCategoriesStore';

import { selectAppLocationSelectValue } from '@store/useAppStore';

import { useTopFailureCauseCategoriesQuery } from '../../services/useTopFailureCauseCategoriesQuery';
import NoData from '../NoData/NoData';
import StatisticWrapper from '../StatisticWrapper/StatisticWrapper';
import style from './style.module.scss';

const TopFailureCauseCategories = () => {
  const { customerId } = useParams();

  const { t } = useTranslation();
  const { setStatusFilter } = useFailureCauseCategoriesActions();

  const locationSelectValue = selectAppLocationSelectValue();
  const statusFilter = selectFailureCauseCategoriesStatusFilter();

  const { data = [], isLoading } = useTopFailureCauseCategoriesQuery({
    locationIds:
      locationSelectValue.level_name === 'allFacilities' ? undefined : locationSelectValue?.filterIds,
    statusType: statusFilter,
  });

  const options: Highcharts.Options = {
    title: { text: '' },
    legend: { enabled: false },
    credits: { enabled: false },
    plotOptions: {
      bar: {
        pointWidth: 25,
      },
    },
    xAxis: {
      type: 'category',
      className: style.axisLabel,
      labels: {
        useHTML: true,
      },
    },
    yAxis: {
      title: {
        text: '',
      },
      className: style.axisLabel,
    },
    tooltip: {
      format: '{point.name}: <b>{point.y}</b>',
    },
    accessibility: { enabled: false },
    series: [
      {
        type: 'bar',
        color: '#3C97AA',
        data,
      },
    ],
  };

  const failureCauseCategoriesStatusTabItems = getFailureCauseCategoriesStatusTabItems(t);

  return (
    <StatisticWrapper
      title={t('Top Failure Cause Categories')}
      link={`/customer/${customerId}/executive-summary/failure-cause-categories`}
      rightElement={
        <Tabs
          id="top-failure-cause-categories-type"
          value={statusFilter}
          tabsItems={failureCauseCategoriesStatusTabItems}
          type="woHelperText"
          onChange={setStatusFilter}
        />
      }
    >
      <>
        {isLoading && <SkeletonBlock className={style.skeleton} />}
        {!isLoading && !!data.length && (
          <HighchartsReact
            highcharts={Highcharts}
            options={options}
            containerProps={{ className: cx(style.topFailureCauseCategoriesChart, 'printHighchartsChart') }}
          />
        )}
        {!isLoading && !data.length && <NoData />}
      </>
    </StatisticWrapper>
  );
};

export default TopFailureCauseCategories;
